import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import VerifyPatientContact from "@/components/AdmitPatient/Modals/VerifyPatientContact";
import VerifyOTP from "@/components/AdmitPatient/Modals/VerifyOTP";

import { IAddPatient, IPatientExistOrNotModule } from "@/models/doctor";
import { IMobileVerify, IOtpVerify } from "@/models/onboarding";
import { IModalKeyEnums, IUserPermissionEnums } from "@/models/permission";

import {
  fetchDoctorHospitalListing,
  fetchHospitalRoomsListing,
  ipDashboardDetails,
} from "@/store/slices/doctor/ipDashboard";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  resetPatientDetailErrorMessages,
  setModuleName,
} from "@/store/slices/doctor/checkMobilePatientExistOrNot";
import { setAddPatient } from "@/store/slices/doctor/topNavigation";

import { INotifyEnum, notify } from "@/utils/toaster";
import { IAPIStatusCode } from "@/utils/apis/APIEndpointConfig";
import useMobileCheckPatientCreate from "@/utils/hooks/useMobileCheckPatientCreate";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";

const PreAdmitPatientPopUps: React.FC = () => {
  const [phone, setPhone] = useState({
    number: "",
    countryCode: "",
  });
  const [isBtnLoading, setIsBtnLoading] = useState<boolean>(false);
  const [isVerifyPatient, setIsVerifyPatient] = useState(false);

  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();

  const { selectedHospitalId, hospitals } = useAppSelector(ipDashboardDetails);
  const { checkNumberError, moduleName } = useAppSelector(
    (state) => state.patientExistOrNot
  );
  const { mobileCheck, otpVerify } = useAppSelector(
    (state) => state.topNavigation.addPatient
  );

  const dispatch = useAppDispatch();

  const permissionRestrictionHandler = usePermissionRestriction();

  const {
    checkMobileNumberDetail,
    createPatientAfterVerifyOtp,
    sendOtpToCreatePatient,
  } = useMobileCheckPatientCreate({
    mobileNo: phone.number,
  });

  const openCloseMobileCheckOTPVerify = (
    field: IAddPatient,
    value: boolean
  ) => {
    dispatch(
      setAddPatient({
        field,
        value,
      })
    );
  };

  const checkMobileNumberSubmission = (
    values: IMobileVerify,
    countryCode: string
  ) => {
    const isPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.USER,
      IUserPermissionEnums.SEARCH_PATIENT
    );
    if (!isPermissionGranted) return;

    setIsBtnLoading(true);
    try {
      setPhone({ ...phone, number: values.mobileNo, countryCode });
      checkMobileNumberDetail(values.mobileNo, countryCode);
      dispatch(setModuleName(IPatientExistOrNotModule.ADMIT_PATIENT));
      dispatch(resetPatientDetailErrorMessages());
    } catch (error) {
      console.log(error);
    } finally {
      setIsBtnLoading(false);
    }
  };

  const otpSubmitHandler = (value: IOtpVerify) => {
    setIsBtnLoading(true);
    try {
      createPatientAfterVerifyOtp(value.otp);
      openCloseMobileCheckOTPVerify(IAddPatient.OTP_VERIFY, false);
      openCloseMobileCheckOTPVerify(IAddPatient.MOBILE_CHECK, false);
      if (selectedHospitalId)
        dispatch(
          fetchHospitalRoomsListing({
            hospitalId: selectedHospitalId,
            isAdmitPatient: true,
            available: true,
          })
        );
      if (isMobile || isTablet) {
        navigate("/admit-patient");
      } else {
        openCloseMobileCheckOTPVerify(IAddPatient.ADMIT_PATIENT, true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsBtnLoading(false);
    }
  };

  useEffect(() => {
    setIsVerifyPatient(mobileCheck);
  }, [mobileCheck]);

  useEffect(() => {
    if (moduleName === IPatientExistOrNotModule.ADMIT_PATIENT) {
      if (!checkNumberError) return;
      const { message, statusCode } = checkNumberError;
      if (statusCode === IAPIStatusCode.BAD_REQUEST) {
        sendOtpToCreatePatient(phone.countryCode);
        openCloseMobileCheckOTPVerify(IAddPatient.OTP_VERIFY, true);

        //close admit-patient modal
        setIsVerifyPatient(false);
      } else if (statusCode === IAPIStatusCode.FORBIDDEN) {
        notify(INotifyEnum.ERROR, message);
      } else if (statusCode === IAPIStatusCode.SUCCESS) {
        if (isMobile || isTablet) {
          navigate("/admit-patient");
        } else {
          openCloseMobileCheckOTPVerify(IAddPatient.ADMIT_PATIENT, true);
        }
        if (selectedHospitalId)
          dispatch(
            fetchHospitalRoomsListing({
              hospitalId: selectedHospitalId,
              isAdmitPatient: true,
              available: true,
            })
          );

        openCloseMobileCheckOTPVerify(IAddPatient.MOBILE_CHECK, false);
      }
    }
  }, [checkNumberError, moduleName]);

  useEffect(() => {
    if (hospitals?.length === 0) {
      dispatch(fetchDoctorHospitalListing());
    }
  }, []);

  return (
    <>
      <VerifyPatientContact
        isOpen={isVerifyPatient}
        closeHandler={() => {
          setIsVerifyPatient(false);
          openCloseMobileCheckOTPVerify(IAddPatient.MOBILE_CHECK, false);
          dispatch(resetPatientDetailErrorMessages());
          dispatch(setModuleName(""));
        }}
        mobileSubmitHandler={checkMobileNumberSubmission}
      />
      {otpVerify && (
        <VerifyOTP
          isBtnLoading={isBtnLoading}
          handleOTPSubmit={otpSubmitHandler}
          mobileNo={phone.number}
          navigateToMobileVerify={() => {
            setIsVerifyPatient(true);
            openCloseMobileCheckOTPVerify(IAddPatient.MOBILE_CHECK, true);
            openCloseMobileCheckOTPVerify(IAddPatient.OTP_VERIFY, false);
            dispatch(resetPatientDetailErrorMessages());
          }}
          onClose={() => {
            openCloseMobileCheckOTPVerify(IAddPatient.MOBILE_CHECK, false);
            openCloseMobileCheckOTPVerify(IAddPatient.OTP_VERIFY, false);
            dispatch(resetPatientDetailErrorMessages());
          }}
          resendOTP={() => sendOtpToCreatePatient(phone.countryCode)}
        />
      )}
    </>
  );
};

export default PreAdmitPatientPopUps;
